/**
 * NEN 1010 rule pack (Netherlands, dwellings), after NEN 1010:2020. Sources and open points:
 * docs/requirements/nen1010.md. Every rule specific to the Netherlands is `verified: false`
 * until it has been checked against the text of the standard and with installers. Dutch texts
 * use the terms of the Netherlands (groep, aardlekschakelaar, wandcontactdoos).
 */
import type { CircuitInfo } from '@elec/analysis';
import { getSymbol } from '@elec/symbols';
import {
  byId,
  circuitRcds,
  DEDICATED,
  earthRule,
  ids,
  name,
  NO_EARTH,
  rcdType,
  rule,
  sensitivityMa,
  variant,
  type Rule,
} from './core.ts';
import { COMMON } from './common.ts';

const rcd30 = (c: CircuitInfo) => c.rcds.filter((r) => sensitivityMa(r) <= 30);
/** Loads with DC or high-frequency fault currents: a type AC RCD may not see their faults. */
const ELECTRONIC = new Set([
  'iec.washing-machine',
  'iec.dryer',
  'iec.dishwasher',
  'iec.cooker',
  'iec.ev-charger',
  'iec.heat-pump',
]);

const NL = byId([
  rule(
    {
      id: 'NL-RCD-00',
      severity: 'error',
      title: {
        fr: 'Prises ou éclairage sans différentiel 30 mA',
        en: 'Sockets or lighting without a 30 mA RCD',
        nl: 'Wandcontactdozen of verlichting zonder aardlekschakelaar van 30 mA',
      },
      why: {
        fr: 'Les prises de courant jusqu’à 32 A à usage général et les points d’éclairage des pièces d’habitation sont protégés par un différentiel 30 mA (411.3.3).',
        en: 'General-use sockets up to 32 A and lighting points in living spaces are protected by a 30 mA RCD (411.3.3).',
        nl: 'Wandcontactdozen tot 32 A voor algemeen gebruik en aansluitpunten voor verlichting in ruimten met een woonfunctie worden beveiligd door een aardlekschakelaar van ten hoogste 30 mA (411.3.3).',
      },
      fix: {
        fr: 'Raccordez le circuit en aval d’un différentiel 30 mA.',
        en: 'Connect the circuit downstream of a 30 mA RCD.',
        nl: 'Sluit de groep aan achter een aardlekschakelaar van 30 mA.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits
        .filter((c) => c.points.some((p) => p.kind === 'socket' || p.kind === 'light') && !rcd30(c).length)
        .map((c) => ({
          targets: ids(c),
          sheetId: c.sheet.id,
          message: {
            fr: `Circuit ${name(c)} : prises ou éclairage sans différentiel 30 mA`,
            en: `Circuit ${name(c)}: sockets or lighting without a 30 mA RCD`,
            nl: `Groep ${name(c)}: wandcontactdozen of verlichting zonder aardlekschakelaar van 30 mA`,
          },
        })),
  ),
  rule(
    {
      id: 'NL-RCD-01',
      severity: 'warning',
      title: {
        fr: 'Plus de 4 circuits sous un différentiel 30 mA',
        en: 'More than 4 circuits under a 30 mA RCD',
        nl: 'Meer dan 4 eindgroepen achter een aardlekschakelaar van 30 mA',
      },
      why: {
        fr: 'Au plus 4 circuits terminaux par différentiel 30 mA, pour limiter les déclenchements intempestifs et leurs conséquences.',
        en: 'At most 4 final circuits per 30 mA RCD, to limit nuisance tripping and what it switches off.',
        nl: 'Hoogstens 4 eindgroepen per aardlekschakelaar van 30 mA, om ongewenst uitschakelen en de gevolgen ervan te beperken.',
      },
      fix: {
        fr: 'Ajoutez un différentiel 30 mA (ou des disjoncteurs différentiels) et répartissez les circuits.',
        en: 'Add a 30 mA RCD (or RCBOs) and spread the circuits.',
        nl: 'Voeg een aardlekschakelaar (of aardlekautomaten) toe en verdeel de groepen.',
      },
      verified: false,
    },
    ({ a, project }) =>
      [...a.byRcd].flatMap(([id, list]) => {
        const r = project.elements[id];
        const n = list.filter((c) => c.rcds[0]?.id === id).length;
        if (!r || sensitivityMa(r) > 30 || n <= 4) return [];
        return [
          {
            targets: [id],
            sheetId: r.sheetId,
            message: {
              fr: `${r.tag} : ${n} circuits (max. 4)`,
              en: `${r.tag}: ${n} circuits (max. 4)`,
              nl: `${r.tag}: ${n} eindgroepen (max. 4)`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'NL-RCD-02',
      severity: 'warning',
      title: {
        fr: 'Un seul différentiel 30 mA coupe toute l’installation',
        en: 'One 30 mA RCD switches off the whole installation',
        nl: 'Eén aardlekschakelaar van 30 mA schakelt de hele installatie uit',
      },
      why: {
        fr: 'À partir de deux circuits terminaux, un seul différentiel 30 mA ne peut pas couper toute l’installation : il en faut au moins deux.',
        en: 'From two final circuits on, a single 30 mA RCD may not switch off the whole installation: at least two are needed.',
        nl: 'Vanaf twee eindgroepen mag één aardlekschakelaar van 30 mA niet de hele installatie uitschakelen: er zijn er minstens twee nodig.',
      },
      fix: {
        fr: 'Répartissez les circuits sur deux différentiels 30 mA ou plus.',
        en: 'Spread the circuits over two or more 30 mA RCDs.',
        nl: 'Verdeel de groepen over twee of meer aardlekschakelaars van 30 mA.',
      },
      verified: false,
    },
    ({ a }) => {
      if (a.circuits.length < 2) return [];
      const all = circuitRcds(a).filter(
        (r) => sensitivityMa(r) <= 30 && a.circuits.every((c) => c.rcds.some((x) => x.id === r.id)),
      );
      return all.map((r) => ({
        targets: [r.id],
        sheetId: r.sheetId,
        message: {
          fr: `${r.tag} : protège les ${a.circuits.length} circuits`,
          en: `${r.tag}: protects all ${a.circuits.length} circuits`,
          nl: `${r.tag}: beveiligt alle ${a.circuits.length} groepen`,
        },
      }));
    },
  ),
  rule(
    {
      id: 'NL-RCD-03',
      severity: 'warning',
      title: {
        fr: 'Différentiel de type AC sur une charge électronique',
        en: 'Type AC RCD on an electronic load',
        nl: 'Aardlekschakelaar type AC op een elektronische belasting',
      },
      why: {
        fr: 'Le type de différentiel suit les courants de défaut possibles (531.3.2) : lave-linge, sèche-linge, lave-vaisselle, plaque à induction et pompe à chaleur demandent au moins le type A (F pour la vitesse variable), une borne de recharge le type A avec détection 6 mA DC ou le type B.',
        en: 'The RCD type follows the possible fault currents (531.3.2): washing machine, dryer, dishwasher, induction hob and heat pump need at least type A (F for variable speed), an EV charger type A with 6 mA DC detection or type B.',
        nl: 'Het type aardlekschakelaar volgt uit de mogelijke foutstromen (531.3.2): wasmachine, droger, vaatwasser, inductiekookplaat en warmtepomp vragen minstens type A (F bij toerenregeling), een laadpunt type A met 6 mA DC-detectie of type B.',
      },
      fix: {
        fr: 'Utilisez un différentiel de type A, F ou B selon l’appareil.',
        en: 'Use a type A, F or B RCD depending on the appliance.',
        nl: 'Gebruik een aardlekschakelaar type A, F of B naargelang het toestel.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const load = c.points.find((p) => ELECTRONIC.has(p.element.symbolId));
        const rs = c.rcds;
        if (!load || !rs.length || rs.some((r) => rcdType(r) !== 'AC')) return [];
        const what = getSymbol(load.element.symbolId)!.name;
        return [
          {
            targets: [c.device.id, ...rs.map((r) => r.id)],
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} (${what.fr.toLowerCase()}) : différentiel de type AC`,
              en: `Circuit ${name(c)} (${what.en.toLowerCase()}): type AC RCD`,
              nl: `Groep ${name(c)} (${what.nl.toLowerCase()}): aardlekschakelaar type AC`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'NL-CIR-01',
      severity: 'warning',
      title: {
        fr: 'Appareil sans circuit propre',
        en: 'Appliance without its own circuit',
        nl: 'Toestel zonder eigen groep',
      },
      why: {
        fr: 'Les gros appareils fixes (cuisson, four, lave-linge, sèche-linge, lave-vaisselle, chauffe-eau, borne de recharge, pompe à chaleur) ont chacun leur propre circuit.',
        en: 'Large fixed appliances (hob, oven, washing machine, dryer, dishwasher, water heater, EV charger, heat pump) each have their own circuit.',
        nl: 'Grote vaste toestellen (kookplaat, oven, wasmachine, droger, vaatwasser, boiler, laadpunt, warmtepomp) hebben elk een eigen groep.',
      },
      fix: {
        fr: 'Placez l’appareil seul sur un circuit.',
        en: 'Put the appliance alone on a circuit.',
        nl: 'Plaats het toestel alleen op een groep.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const big = c.points.filter((p) => DEDICATED.has(p.element.symbolId));
        if (!big.length || c.points.length === 1) return [];
        return [
          {
            targets: [c.device.id, ...big.map((p) => p.element.id)],
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ${big.map((p) => p.element.tag).join(', ')} partage le circuit`,
              en: `Circuit ${name(c)}: ${big.map((p) => p.element.tag).join(', ')} shares the circuit`,
              nl: `Groep ${name(c)}: ${big.map((p) => p.element.tag).join(', ')} deelt de groep`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'NL-CIR-02',
      severity: 'warning',
      title: {
        fr: 'Prise sans contact de terre',
        en: 'Socket without earth contact',
        nl: 'Wandcontactdoos zonder randaarde',
      },
      why: {
        fr: 'Dans une installation neuve, les prises de courant ont un contact de terre raccordé au conducteur de protection.',
        en: 'In a new installation, socket outlets have an earth contact connected to the protective conductor.',
        nl: 'In een nieuwe installatie hebben wandcontactdozen randaarde, aangesloten op de beschermingsleiding.',
      },
      fix: {
        fr: 'Remplacez par une prise avec terre.',
        en: 'Replace with an earthed socket.',
        nl: 'Vervang door een wandcontactdoos met randaarde.',
      },
      verified: false,
    },
    ({ project }) =>
      Object.values(project.elements)
        .filter((e) => NO_EARTH.has(e.symbolId) && project.sheets[e.sheetId]?.kind === 'single-line')
        .map((e) => ({
          targets: [e.id],
          sheetId: e.sheetId,
          message: {
            fr: `${e.tag || 'Prise'} : sans terre`,
            en: `${e.tag || 'Socket'}: no earth`,
            nl: `${e.tag || 'Wandcontactdoos'}: geen randaarde`,
          },
        })),
  ),
  earthRule('NL-EARTH-01', {
    why: {
      fr: 'En schéma TT, la prise de terre figure au schéma et sa résistance respecte RA × IΔn ≤ 50 V, avec le différentiel le moins sensible (411.5.3). En TN-C-S, la terre vient du gestionnaire de réseau.',
      en: 'In a TT system the earth electrode appears on the diagram and its resistance meets RA × IΔn ≤ 50 V, with the least sensitive RCD (411.5.3). In TN-C-S the earth comes from the DSO.',
      nl: 'In een TT-stelsel staat de aardelektrode op het schema en voldoet haar weerstand aan RA × IΔn ≤ 50 V, met de minst gevoelige aardlekschakelaar (411.5.3). Bij TN-C-S levert de netbeheerder de aarde.',
    },
    fix: {
      fr: 'Sans prise de terre au schéma : bouton « Ajouter la prise de terre » ci-dessous, ou cherchez « terre » dans la palette. Saisissez ensuite la valeur mesurée (Contrôle › Mesures et essais). Trop élevée : améliorez la prise de terre et mesurez à nouveau. Ou vérifiez le schéma de liaison à la terre (TN-C-S : terre du gestionnaire de réseau).',
      en: 'With no electrode on the diagram: the “Add the earth electrode” button below, or search “earth” in the palette. Then enter the measured value (Inspection › Test results). Too high: improve the electrode and measure again. Or check the earthing system (TN-C-S: the DSO’s earth).',
      nl: 'Zonder aardelektrode op het schema: de knop “Aardverbinding toevoegen” hieronder, of zoek “aard” in het palet. Vul daarna de gemeten waarde in (Keuring › Metingen en testen). Te hoog: verbeter de aarding en meet opnieuw. Of controleer het aardingsstelsel (TN-C-S: aarde van de netbeheerder).',
    },
    verified: false,
  }),
]);

const PLAN = variant(COMMON['R-DOC-03']!, {
  severity: 'warning',
  why: {
    fr: 'Quand un plan est joint, chaque point du schéma y figure avec le même repère, et inversement.',
    en: 'When a plan is included, every point of the diagram appears on it with the same code, and vice versa.',
    nl: 'Als er een installatietekening is, staat elk punt van het schema erop met dezelfde code, en omgekeerd.',
  },
});
const ROOMS = variant(COMMON['R-PLAN-01']!, {
  why: {
    fr: 'La pièce d’un point rend le plan lisible pour le client et l’inspection.',
    en: 'A point’s room makes the plan readable for the client and the inspection.',
    nl: 'De ruimte van een punt maakt de tekening leesbaar voor de klant en de inspectie.',
  },
});
const DROP = variant(COMMON['R-CALC-03']!, {
  why: {
    fr: 'Chute de tension au-delà des valeurs recommandées (réseau public BT) : 3 % pour l’éclairage, 5 % pour les autres usages (NEN 1010, 525, d’après l’IEC 60364-5-52 annexe G).',
    en: 'Voltage drop beyond the recommended values (public LV supply): 3 % for lighting, 5 % for other uses (NEN 1010, 525, after IEC 60364-5-52 Annex G).',
    nl: 'Spanningsval boven de aanbevolen waarden (openbaar laagspanningsnet): 3 % voor verlichting, 5 % voor andere toepassingen (NEN 1010, 525, naar IEC 60364-5-52 bijlage G).',
  },
});

/** The NEN 1010 pack, in the order findings are listed. */
export const NEN1010_RULES: Rule[] = [
  COMMON['R-DOC-02']!,
  NL['NL-RCD-00']!,
  NL['NL-RCD-01']!,
  NL['NL-RCD-02']!,
  NL['NL-RCD-03']!,
  NL['NL-CIR-01']!,
  COMMON['R-CIR-04']!,
  COMMON['R-DEV-01']!,
  NL['NL-CIR-02']!,
  COMMON['R-CIR-06']!,
  COMMON['R-CIR-07']!,
  COMMON['R-DOC-01']!,
  PLAN,
  COMMON['R-TOPO-01']!,
  COMMON['R-TOPO-02']!,
  NL['NL-EARTH-01']!,
  ROOMS,
  COMMON['R-CTL-01']!,
  COMMON['R-CTL-02']!,
  COMMON['R-CTL-03']!,
  COMMON['R-DOC-04']!,
  COMMON['R-CALC-01']!,
  COMMON['R-CALC-02']!,
  DROP,
  COMMON['R-CALC-04']!,
  COMMON['R-CALC-05']!,
  COMMON['R-CALC-00']!,
  COMMON['R-ML-01']!,
  COMMON['R-SRV-01']!,
];
